import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { useMutation } from '@apollo/client';
import { EditableSectionButtons, EditableSectionTitle, InfoCell, InfoSection } from "components/CPCommittedCapacity/components/CommonComponents";
import { TooltipTitle } from 'components/CPCommittedCapacity/tabs/CommitmentsTab';
import TextEntry from '@arrive/text-entry';

import { UPDATE_COMMITMENT_RECIPIENT_EMAILS } from 'api/mutations';

import { COMMITTED_CAPACITY_PENDO_ROOT } from 'constants/cPCommittedCapacityConstants';

import styles from 'components/CPCommittedCapacity/components/RateconLoadTenderSection/RateconLoadTenderSection.scss'

const rateConLabel = 'Ratecon Recipients';
const loadTenderLabel = 'Load Tender Recipients';

export default function RateconLoadTenderSection({ carrierId, rateConEmail, loadTenderEmail, loadingCommitment }: { carrierId: number; rateConEmail: string; loadTenderEmail: string; loadingCommitment: boolean; }) {
  const [edit, setEdit] = useState(false)
  const [rateconRecipients, setRateconRecipients] = useState<string[]>([''])
  const [rateconInputVal, setRateconInputVal] = useState<string>(formatEmails(rateconRecipients))
  const [rateconRecipientsError, setRateconRecipientsError] = useState<string | undefined>()
  const [loadTenderRecipients, setLoadTenderRecipients] = useState<string[]>([''])
  const [loadTenderInputVal, setLoadTenderInputVal] = useState<string>(formatEmails(loadTenderRecipients))
  const [loadTenderRecipientsError, setLoadTenderRecipientsError] = useState<string | undefined>()


  useEffect(() => {
    if (rateConEmail) {
      setRateconRecipients(rateConEmail?.split(","))
    }
    if (loadTenderEmail) {
      setLoadTenderRecipients(loadTenderEmail?.split(","))
    }
  }, [loadTenderEmail, rateConEmail])

  useEffect(() => {
    if(edit){
      setRateconInputVal(formatEmails(rateconRecipients))
      setRateconRecipientsError(undefined)
      setLoadTenderInputVal(formatEmails(loadTenderRecipients))
      setLoadTenderRecipientsError(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit])

  function formatEmails(emails : string[]){
    return emails[0] ? emails.reduce((formattedEmails, email) => `${formattedEmails}, ${email}`) : ""
  }

  const cleanEmails = (emails: string) => {
    const cleanedEmails = emails.replace(/\s/g, '').split(',')
    if(cleanedEmails[cleanedEmails.length - 1] === ""){
      cleanedEmails.splice(-1, 1)
    }
    return cleanedEmails
  }

  const validateEmails = (emails: string[], setErr?: (err: string) => void) => {
    //eslint-disable-next-line
    const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const allValid = emails[0] && emails.reduce((valid, email) => re.test(email) && valid, true)
    if(!allValid){
      setErr("Please enter one or more valid comma separated email address.")
    }else {
      setErr(undefined)
    }
  }

  const setInvalidEmailErrors = (invalidEmails: string[], cleanRateCon: string[], cleanLoadTender: string[]) => {
    const rateConInvalidEmails: string[] = [];
    const loadTenderInvalidEmails: string[] = [];
    invalidEmails.forEach((email: string) => {
      if (cleanRateCon.includes(email)) {
        rateConInvalidEmails.push(email)
      }
      if (cleanLoadTender.includes(email)) {
        loadTenderInvalidEmails.push(email)
      }
    })
    if (rateConInvalidEmails.length) {
      setRateconRecipientsError(`Address not linked to Carrier Portal account (${rateConInvalidEmails.join(', ')})`)
    }
    if (loadTenderInvalidEmails.length) {
      setLoadTenderRecipientsError(`Address not linked to Carrier Portal account (${loadTenderInvalidEmails.join(', ')})`)
    }
  }

  const handleRateConRecipients = (emails: string) => {
    setRateconInputVal(emails)
    validateEmails(cleanEmails(emails), setRateconRecipientsError)
  }

  const handleLoadTenderRecipients = (emails: string) => {
    setLoadTenderInputVal(emails)
    validateEmails(cleanEmails(emails), setLoadTenderRecipientsError)
  }

  const [updateEmails, { loading }] = useMutation(UPDATE_COMMITMENT_RECIPIENT_EMAILS, {
    onCompleted: (res: any) => {
      const cleanRateCon = cleanEmails(rateconInputVal);
      const cleanLoadTender = cleanEmails(loadTenderInputVal);
      if (res.updateCommitmentEmailRecipients.success) {
        setRateconRecipients(cleanRateCon)
        setLoadTenderRecipients(cleanLoadTender)
        setEdit(false)
        if (window.pendo) {
          window.pendo.track(`${COMMITTED_CAPACITY_PENDO_ROOT} Save RateCon / Load Tender Recipients`, {
            RateConValue: cleanRateCon,
            LoadTenderValue: cleanLoadTender
          });
        }
      }

      if (res.updateCommitmentEmailRecipients.invalidEmails.length) {
        setInvalidEmailErrors(res.updateCommitmentEmailRecipients.invalidEmails, cleanRateCon, cleanLoadTender)
      }

    }
  })

  const save = () => {
    if(loadTenderRecipientsError || rateconRecipientsError){
      return
    }
    const cleanRateCon = cleanEmails(rateconInputVal);
    const cleanLoadTender = cleanEmails(loadTenderInputVal);

    updateEmails({
      variables: {
        carrierId: carrierId,
        rateConEmail: cleanRateCon.join(','),
        loadTenderEmail: cleanLoadTender.join(',')
      }})

  }

  const onEdit = () => {
    if (window.pendo) {
      window.pendo.track(`${COMMITTED_CAPACITY_PENDO_ROOT} Edit RateCon / Load Tender Recipients`, {
        Action: !edit ? 'Edit' : 'Close'
      });
    }

    setEdit(!edit);
  };

  const onCancelEdit = () => {
    if (window.pendo) {
      window.pendo.track(`${COMMITTED_CAPACITY_PENDO_ROOT} Cancel Edit RateCon / Load Tender Recipients`, { });
    }

    setEdit(false);
  }

  return (
    <>
      <EditableSectionTitle title={'Recipients'} edit={edit} onEdit={onEdit}/>
      {
        edit ?
          <InfoSection className={styles.rateConLoadTenderContainer}>
            <div className={styles.rateConInputContainer}>
              <TextEntry
                data-testid={'ratecon-input'}
                classes={{ container: cx(styles.cellTextArea), input: styles.recipentsInput}}
                onChange={(e) => handleRateConRecipients(e.target.value)}
                value={rateconInputVal}
                label={rateConLabel}
              />
              <div className={styles.recipientsError} data-testid={'ratecon-error'}>{rateconRecipientsError}</div>
            </div>
            <div className={styles.loadTenderInputContainer}>
              <TextEntry
                data-testid={'load-tender-input'}
                classes={{ container: cx(styles.cellTextArea), input: styles.recipentsInput}}
                onChange={(e) => handleLoadTenderRecipients(e.target.value)}
                value={loadTenderInputVal}
                label={loadTenderLabel}
              />
              <div className={styles.recipientsError} data-testid={'load-tender-error'}>{loadTenderRecipientsError}</div>
            </div>
            <EditableSectionButtons onCancel={onCancelEdit} onSave={save} disabled={!!rateconRecipientsError || !!loadTenderRecipientsError} className={styles.rateConLoadTenderEditButtons} loading={loading}/>
          </InfoSection>
          :
          <InfoSection className={styles.rateConLoadTenderReadbackContainer}>
            <InfoCell
              title={TooltipTitle(rateConLabel, 'The email addresses that will receive the Rate Confirmation after a tender is Booked')}
              content={formatEmails(rateconRecipients)}
              testIdString={rateConLabel}
              className={styles.noCapitalize}
              loading={loadingCommitment}
            />
            <InfoCell
              title={TooltipTitle(loadTenderLabel, 'The email addresses that will receive actionable, time-sensitive tenders')}
              content={formatEmails(loadTenderRecipients)}
              testIdString={loadTenderLabel}
              className={styles.noCapitalize}
              loading={loadingCommitment}
            />
          </InfoSection>
      }
    </>
  )
}
